import { useState } from '#imports'
import {
  type MaybeRefOrGetter,
  computed,
  onUnmounted,
  toValue,
  watch,
} from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import { getCondensedFunnelCategory } from './getCondensedFunnelCategory'

/**
 * Single source of truth for "is the condensed funnel experiment active for
 * the current product?".
 *
 * - **Readers** (`LargePicker`, `StepLayout`, the funnel container) call it
 *   without arguments: safe from anywhere, no fetch. The experiment flag is
 *   resolved once via `useState` (idempotent — shared across every caller).
 * - **PDP containers** pass the product's category id to maintain the shared
 *   in-scope flag (watched, so it stays in sync when the product loads or
 *   changes). Non-PDP consumers never trigger a product fetch — with no
 *   product id in the route, a fetch would 404
 *   (`/product-page/products/undefined`).
 *
 * SSR and watchers care: `immediate: true` evaluates synchronously during
 * setup; during SSR the containers' product data is awaited before children
 * render, so readers always see the correct flag. The watcher is bound to the
 * calling component and disposed with it; `onUnmounted` resets the flag so a
 * stale `true` can't restyle non-PDP consumers after client-side navigation
 * (`useState` persists across routes).
 *
 * This replaces the raw `experiments['experiment.ppCondensedFunnel']` reads
 * that were duplicated across 4 components with two divergent scopes.
 */
export function useCondensedFunnelExperiment(
  categoryId?: MaybeRefOrGetter<string | undefined>,
) {
  const isExperimentEnabled = useState('pp-condensed-funnel-enabled', () => {
    const experiments = useExperiments()

    return experiments['experiment.ppCondensedFunnel'] === 'withCondensedFunnel'
  })

  const isInScopeCategory = useState<boolean>(
    'pp-condensed-funnel-in-scope',
    () => false,
  )

  if (categoryId !== undefined) {
    watch(
      () => toValue(categoryId),
      (id) => {
        isInScopeCategory.value = Boolean(
          id && getCondensedFunnelCategory(id) !== undefined,
        )
      },
      { immediate: true },
    )

    // Reset when leaving the PDP: the shared state outlives the route.
    onUnmounted(() => {
      isInScopeCategory.value = false
    })
  }

  return computed(() => isExperimentEnabled.value && isInScopeCategory.value)
}
